"use client";

import Image from "next/image";
import { useState } from "react";
import isEmpty from "lodash/isEmpty";
import { useAtomValue } from "jotai";
import { FiExternalLink } from "react-icons/fi";
import { HiOutlineClipboardDocument } from "react-icons/hi2";
import { PiEye, PiDownloadSimpleBold, PiCheck } from "react-icons/pi";
import { Avatar } from "@/components/ui/avatar";
import { Tooltip } from "@/components/ui/tooltip";
import { Title, Text } from "@/components/ui/text";
import { getRelativeTime } from "@/utils/get-relative-time";
import {
  dataAtom,
  messageIdAtom,
} from "@/app/shared/support/inbox/message-list";
import { useCopyToClipboard } from "@/hooks/use-copy-to-clipboard";
import { DotSeparator } from "@/app/shared/support/inbox/message-details";
import { Message, User } from "@/types/chatroom/chatroom";
import { useSession } from "next-auth/react";
import avatarAddress from "@/functions/avatarAddress";
import { BASE_URL } from "@/lib/axios";
import { saveAs } from "file-saver";

export default function MessageBody({
  messageData,
  userData,
}: {
  messageData: Message;
  userData: User | null;
}) {
  const handleDownload = (name: string) => {
    saveAs(BASE_URL + messageData.image, name);
  };
  const data = useAtomValue(dataAtom);
  const messageId = useAtomValue(messageIdAtom);
  const [isCopied, setIsCopied] = useState(false);
  const [state, copyToClipboard] = useCopyToClipboard();
  const { data: session } = useSession();
  const userId = session?.user.userId;
  const message = data.find((m) => m.id === messageId);
  // const message = {};
  const initials = `${message?.username}${message?.lastName.charAt(0)}`;
  const handleCopyToClipboard = () => {
    copyToClipboard(message?.id as string);
    if (!state.error && state.value) {
      setIsCopied(() => true);
      setTimeout(() => {
        setIsCopied(false);
      }, 3000);
    }
  };
  return (
    <div
      dir={session?.user.id === messageData.owner ? "rtl" : "ltr"}
      className={"flex gap-2 "}
    >
      <div
        className={
          "grid grid-cols-[32px_1fr] items-start gap-3 lg:gap-4 xl:grid-cols-[48px_1fr] "
        }
      >
        <Avatar
          name="John Doe"
          src={avatarAddress(messageData.owner)}
          initials={initials}
          className="!h-8 !w-8 bg-[#70C5E0] font-medium text-white xl:!h-11 xl:!w-11"
        />
      </div>

      <div
        className={
          " mb-3 grid gap-2 leading-relaxed  2xl:mb-4 bg-gray-50 p-2 rounded-lg "
        }
      >
        <div className="-mt-1.5 lg:mt-0">
          <div className={"flex items-center justify-between "}>
            <Title as="h4" className="text-sm font-medium text-primary-light">
              {userData === null
                ? "Admin"
                : messageData.owner === userData.id
                ? userData?.username
                : "Admin"}
            </Title>
          </div>
          <div dir="rtl">
            <Text className="my-2 whitespace-pre-wrap">
              {messageData.content}
            </Text>
          </div>
          <div className="mt-1.5 items-center gap-2 text-xs text-gray-500 lg:flex opacity-50">
            <DotSeparator className="hidden lg:block" />
            <span className="mt-1.5 flex items-center lg:mt-0">
              #
              {userData === null
                ? "Admin"
                : messageData.owner === userData.id
                ? userData?.id
                : "Admin"}{" "}
              <Tooltip
                size="sm"
                rounded="sm"
                placement="top"
                content={() => (isCopied ? "Copied" : "کپی کن")}
              >
                <button type="button" onClick={handleCopyToClipboard}>
                  {isCopied ? (
                    <PiCheck className="ml-1 h-3 w-3" />
                  ) : (
                    <HiOutlineClipboardDocument className="ml-1 h-3 w-3" />
                  )}
                </button>
              </Tooltip>
            </span>
            <DotSeparator className="hidden lg:block" />
            <span>
              Open {getRelativeTime(new Date(messageData?.create_date) as Date)}
            </span>
          </div>
        </div>

        {messageData?.image && (
          <div className="mt-2 grid gap-2 md:grid-cols-2 lg:grid-cols-1">
            <div className="grid grid-cols-[40px_1fr] gap-2.5">
              <figure className="relative h-10 w-10 overflow-hidden rounded">
                <Image
                  fill
                  alt={"عکس"}
                  src={BASE_URL + messageData.image}
                  className="object-contain"
                />
              </figure>

              <div className="text-xs">
                <span className="flex items-center gap-2 font-lexend font-medium text-gray-700"></span>
                <div className="mt-2 flex items-center gap-2">
                  <DotSeparator />
                  <div className="flex items-center gap-2 text-gray-500 transition duration-300 hover:text-gray-900">
                    <PiDownloadSimpleBold className="h-3.5 w-3.5" />{" "}
                    <button
                      onClick={() => {
                        handleDownload(
                          (userData?.username ? userData.username : "image") +
                            messageData.id
                        );
                      }}
                    >
                      دانلود
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
